import React, { useEffect, useState } from "react";
import { Table, DatePicker, Select, Spin, Space } from "antd";
import { useReportApi } from "../../store/reportContext";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import PageNavigation from "../Layout/PageNavigation";
import ExcelExportButton from "./ExcelExportButton";

const { RangePicker } = DatePicker;

const num = (value: number | undefined) =>
    typeof value === "number" ? value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-";

const TopItemsTable: React.FC = () => {
    const { fetchTopItems } = useReportApi();
    const { t } = useTranslation();
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState(false);
    const [type, setType] = useState<"sale" | "purchase" | "return">("sale");
    const [dateRange, setDateRange] = useState<[any, any]>([dayjs().startOf('month'), dayjs()]);

    useEffect(() => {
        setLoading(true);
        fetchTopItems({
            type,
            startDate: dateRange[0].toISOString(),
            endDate: dateRange[1].toISOString(),
            limit: 10,
        }).then(res => {
            setData(res);
            setLoading(false);
        });
    }, [type, dateRange, fetchTopItems]);

    // Buying has no sale price, so there is no profit to show against a purchase.
    const showsProfit = type !== "purchase";

    const columns = [
        { title: t('product'), dataIndex: ['product', 'name'] },
        { title: t('code'), dataIndex: ['product', 'code'] },
        { title: t('qty'), dataIndex: 'totalQty' },
        { title: t('totalAmount'), dataIndex: 'totalAmount' },
        ...(showsProfit ? [
            { title: t('cost'), dataIndex: 'totalCost', render: (value: number) => num(value) },
            {
                title: t('profit'),
                dataIndex: 'totalProfit',
                render: (value: number) => (
                    <span style={{ color: (value || 0) < 0 ? '#dc2626' : (value || 0) > 0 ? '#16a34a' : undefined }}>
                        {num(value)}
                    </span>
                ),
            },
        ] : []),
    ];

    return (
        <>
            <PageNavigation title={t('top_items') as string} />
            <Select
                style={{ marginBottom: 8, width: 140 }}
                value={type}
                onChange={setType}
                options={[
                    { value: "sale", label: t("sales") },
                    { value: "purchase", label: t("purchases") },
                    { value: "return", label: t("returns") }
                ]}
            />
            <Space style={{ marginBottom: 8, marginInlineStart: 8 }} wrap>
                <RangePicker
                    value={dateRange}
                    onChange={dates => {
                        if (dates && dates[0] && dates[1]) setDateRange(dates as [dayjs.Dayjs, dayjs.Dayjs]);
                    }}
                />
                <ExcelExportButton
                    fileName={`top-items-${type}`}
                    sheetName={t('top_items') as string}
                    columns={[
                        { header: t('product') as string, key: (row: any) => row.product?.name || '-', width: 28 },
                        { header: t('code') as string, key: (row: any) => row.product?.code || '-', width: 16 },
                        { header: t('qty') as string, key: 'totalQty', width: 12 },
                        { header: t('totalAmount') as string, key: 'totalAmount', width: 16 },
                        ...(showsProfit ? [
                            { header: t('cost') as string, key: 'totalCost', width: 16 },
                            { header: t('profit') as string, key: 'totalProfit', width: 16 },
                        ] : []),
                    ]}
                    rows={data}
                    footer={[[
                        t('total') as string,
                        '',
                        data.reduce((sum, row) => sum + (row.totalQty || 0), 0),
                        data.reduce((sum, row) => sum + (row.totalAmount || 0), 0),
                        ...(showsProfit ? [
                            data.reduce((sum, row) => sum + (row.totalCost || 0), 0),
                            data.reduce((sum, row) => sum + (row.totalProfit || 0), 0),
                        ] : []),
                    ]]}
                    title={[
                        `${t('top_items')} — ${t(type === 'sale' ? 'sales' : type === 'purchase' ? 'purchases' : 'returns')}`,
                        `${dateRange[0].format('YYYY-MM-DD')} → ${dateRange[1].format('YYYY-MM-DD')}`,
                    ]}
                />
            </Space>
            <Spin spinning={loading}>
                <Table rowKey="_id" columns={columns} dataSource={data} pagination={false} />
            </Spin>
        </>
    );
};

export default TopItemsTable;
